'use client'

import { useState, useCallback } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import { Spinner } from '@/app/components/common/Spinner'

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

interface PdfViewerProps {
  /** URL to the PDF file (presigned S3 URL or blob URL) */
  pdfUrl: string
  /** Callback when the document loads, receives the total number of pages */
  onDocumentLoad?: (numPages: number) => void
  /** Render function for overlays on each page (e.g., field overlays) */
  renderOverlay?: (
    pageNumber: number,
    dimensions: { width: number; height: number }
  ) => React.ReactNode
  /** Width to render each page at (default 800) */
  width?: number
  /** Click handler for a page, receives page number and coordinates relative to page */
  onPageClick?: (pageNumber: number, x: number, y: number) => void
}

interface PageDimensions {
  width: number
  height: number
}

export function PdfViewer({
  pdfUrl,
  onDocumentLoad,
  renderOverlay,
  width = 800,
  onPageClick,
}: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageDimensions, setPageDimensions] = useState<Record<number, PageDimensions>>({})
  const [loading, setLoading] = useState(true)

  const handleDocumentLoadSuccess = useCallback(
    (pdf: { numPages: number }) => {
      setNumPages(pdf.numPages)
      setLoading(false)
      onDocumentLoad?.(pdf.numPages)
    },
    [onDocumentLoad]
  )

  const handlePageLoadSuccess = useCallback(
    (page: { pageNumber: number; originalWidth: number; originalHeight: number }) => {
      const scale = width / page.originalWidth
      setPageDimensions((prev) => ({
        ...prev,
        [page.pageNumber]: {
          width,
          height: page.originalHeight * scale,
        },
      }))
    },
    [width]
  )

  function handlePageClick(e: React.MouseEvent<HTMLDivElement>, pageNumber: number) {
    if (!onPageClick) return

    const target = e.currentTarget
    const rect = target.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    onPageClick(pageNumber, x, y)
  }

  return (
    <div data-component-name="PdfViewer" className="flex flex-col items-center">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-600">Loading PDF...</span>
        </div>
      )}

      <Document
        file={pdfUrl}
        onLoadSuccess={handleDocumentLoadSuccess}
        onLoadError={() => setLoading(false)}
        loading={null}
        error={
          <div className="text-center py-12 text-danger">
            Failed to load PDF. Please try uploading again.
          </div>
        }
      >
        {Array.from({ length: numPages }, (_, i) => {
          const pageNum = i + 1
          const dims = pageDimensions[pageNum]

          return (
            <div key={pageNum} className="mb-4">
              <div
                data-page-number={pageNum}
                className="relative bg-white shadow-md"
                style={{ width }}
              >
                <Page
                  pageNumber={pageNum}
                  width={width}
                  onLoadSuccess={handlePageLoadSuccess}
                  renderAnnotationLayer={true}
                  renderTextLayer={true}
                />

                {/* Click-capture overlay sits above text layer (z-index 2) */}
                {onPageClick && dims && (
                  <div
                    className="absolute inset-0"
                    style={{ zIndex: 3, cursor: 'crosshair' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handlePageClick(e, pageNum)
                    }}
                  />
                )}

                {dims && renderOverlay && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ width: dims.width, height: dims.height, zIndex: 4 }}
                  >
                    <div className="pointer-events-auto">{renderOverlay(pageNum, dims)}</div>
                  </div>
                )}
              </div>

              <div className="text-center text-sm text-gray-500 mt-1 mb-3">
                Page {pageNum} of {numPages}
              </div>
            </div>
          )
        })}
      </Document>
    </div>
  )
}
